import * as React from "react";
import ReactDOM from "react-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Label } from "@progress/kendo-react-labels";
import { filterBy } from "@progress/kendo-data-query";
import { Grid, GridColumn as Column, GridNoRecords } from "@progress/kendo-react-grid";
import * as api from "api/index";
import styles from "../Settings.module.scss";
import { useNavigate } from "react-router";
import { Input, RadioGroup, RadioButton } from "@progress/kendo-react-inputs";
import { FormComboBox } from "components/Matters";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { FiAlertCircle } from "react-icons/fi";
import { QuestionsPopup } from "./SelectQuestionBox";
import CancelImage from "../../../assets/common/redcross.png";
import { useActiveDepartments } from "services/departmentService";
import { useActiveWorkTypes } from "services/workTypeService";
import SubmitButton from "components/common/SubmitButton";
import matterIcon from "../../../assets/casefileIcon.svg";
import ClientIcon from "../../../assets/client.svg";
import { IoSettings } from "react-icons/io5";
import { FaPlus } from "react-icons/fa";
import { GridLoader } from "components/GridLoader";

const RiskAssessment = (props) => {
  const [openForm, setOpenForm] = React.useState(false);
  const navigate = useNavigate();
  const [gridData, setGridData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: departmentData, loading: loadingDepartments } =
    useActiveDepartments();
  const { data: categoryData, loading: loadingCategories } =
    useActiveWorkTypes();
  const [departmentFilter, setDepartmentFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [riskScores, setRiskScores] = useState({
    low: 0.0,
    medium: 0.0,
    high: 0.0,
    riskAssessmentId: null,
  });
  const [type, setType] = useState("Default");
  const [comboBoxState, setComboBoxState] = useState({
    department: null,
    workType: null,
  });
  const [clientType, setClientType] = useState("Person");
  useEffect(() => {
    if (departmentData && !loadingDepartments) {
      setDepartmentFilter(departmentData);
    }
  }, [departmentData, loadingDepartments]);

  useEffect(() => {
    if (categoryData && !loadingCategories) {
      setCategoryFilter([...categoryData]);
    }
  }, [categoryData, loadingCategories]);

  useEffect(() => {
    if (comboBoxState.department) {
      setComboBoxState((prev) => ({
        ...prev,
        workType: null,
      }));
    }
  }, [comboBoxState?.department]);

  useEffect(() => {
    const loadAssessmentData = async () => {
      try {
        setIsLoading(true);
        await getAssessmentData();
      } catch (err) {
        toast.error("Failed to load assessment data");
      } finally {
        setIsLoading(false);
      }
    };
    if (
      type === "Default" ||
      type === "Client" ||
      (type === "Matter" && comboBoxState.workType?.workTypeID)
    ) {
      loadAssessmentData();
    } else {
      setGridData([]);
      setRiskScores({
        low: 0.0,
        medium: 0.0,
        high: 0.0,
        riskAssessmentId: null,
      });
    }
  }, [clientType, comboBoxState.workType, type]);

  const handleComboBoxChange = (e, label) => {
    if (label === "department") {
      setComboBoxState({
        [label]: e.value,
        workType: null,
      });
    } else {
      setComboBoxState({
        ...comboBoxState,
        [label]: e.value,
      });
    }
  };

  const HeaderButton = () => {
    return (
      <div className="d-flex mb-1">
        <Button
          className="common-btn button-small"
          onClick={() => setOpenForm(true)}
          disabled={
            type === "Matter" && !comboBoxState.workType?.workTypeID
          }
        >
          <FaPlus className="me-1" />
          Add
        </Button>
      </div>
    );
  };

  const filterDepartment = (filter) => {
    const data = departmentData?.slice() || [];
    return filterBy(data, filter);
  };

  const departmentfilterChange = (event) => {
    setDepartmentFilter(filterDepartment(event.filter));
  };

  const filterCategory = (filter) => {
    const data = categoryData?.slice() || [];
    return filterBy(data, filter);
  };

  const categoryFilterChange = (event) => {
    setCategoryFilter(filterCategory(event.filter));
  };

  const getAssessmentData = async () => {
    try {
      let data = {};
      if (type === "Default") {
        data = await api.getRiskAssessmentDefault();
      } else if (type === "Client") {
        data = await api.getClientRiskAssessmentSettings(clientType);
      } else if (type === "Matter" && comboBoxState.workType?.workTypeID) {
        data = await api.getMatterRiskAssessmentSettings(
          comboBoxState.workType.workTypeID
        );
      }

      if (data?.data?.data) {
        setGridData(data.data.data.questions || []);
        setRiskScores({
          low: data.data.data.lowRiskScore || 0,
          high: data.data.data.highRiskScore || 0,
          medium: data.data.data.mediumRiskScore || 0,
          riskAssessmentId: data.data.data.riskAssessmentId,
        });
      } else {
        setGridData([]);
        setRiskScores({
          low: 0.0,
          medium: 0.0,
          high: 0.0,
          riskAssessmentId: null,
        });
      }
    } catch (error) {
      setGridData([]);
      toast("Failed to load assessment data");
    }
  };

  const saveAssessmentData = async () => {
    try {
      setIsSubmitting(true);
      const data = {
        lowRiskScore: parseInt(riskScores.low) || 0,
        highRiskScore: parseInt(riskScores.high) || 0,
        mediumRiskScore: parseInt(riskScores.medium) || 0,
        questions: gridData.filter((q) => q.active),
        riskAssessmentId: riskScores.riskAssessmentId,
      };

      if (type === "Default") {
        await api.updateRiskAssessmentDefault({ data });
      } else if (type === "Client") {
        await api.editClientRiskAssessmentSettings(clientType, { data });
      } else if (type === "Matter" && comboBoxState.workType?.workTypeID) {
        await api.editMatterRiskAssessmentSettings(
          comboBoxState.workType.workTypeID,
          { data }
        );
      }
      toast.success("Risk Assessment Data Updated");
    } catch (error) {
      toast.error("Unexpected Error Occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onChangeRiskScores = (e) => {
    setRiskScores({
      ...riskScores,
      [e.target.props.name]: e.value,
    });
  };

  const handleCancelEdit = () => {
    setOpenForm(false);
  };

  const handleRemove = (questionId) => {
    const newData = gridData.map((question) =>
      question.questionId === questionId
        ? { ...question, active: false }
        : question
    );
    setGridData(newData);
  };

  const CancelCell = (props) => {
    return (
      <td className="">
        <Tooltip anchorElement="target" position="top">
          <img
            title="Remove"
            alt="Remove"
            style={{ width: "18px", height: "18px" }}
            onClick={() => handleRemove(props.dataItem.questionId)}
            className="cursor-pointer"
            src={CancelImage}
          />
        </Tooltip>
      </td>
    );
  };

  const isSaveDisabled = () => {
    if (type === "Matter" && !comboBoxState.workType?.workTypeID) return true;
    return (
      isLoading ||
      riskScores.low === null ||
      riskScores.medium === null ||
      riskScores.high === null
    );
  };

  return (
    <div className="h-100 w-100 d-flex flex-column">
      <div className="flex-shrink-0 w-100 mx-auto" style={{ maxWidth: "1400px" }}>
        <div className="mb-2 fw-semibold">Risk Assessment Type</div>
        <div
          className="d-flex justify-content-between align-items-start gap-3"
        >
          <div className="d-flex flex-column w-75 bg-white shadow-sm border rounded-3 p-3" style={{ minHeight: "160px" }} >
            <div className="d-flex flex-wrap flex-row gap-3">
              {[
                { name: "Default", icon: <IoSettings className="text-black fs-5" /> },
                { name: "Client", icon: <img title="Client" src={ClientIcon} alt="Client Icon" width={"20px"} height={"20px"} /> },
                { name: "Matter", icon: <img title="Matter" src={matterIcon} alt="Matter Icon" width={"25px"} height={"24px"} /> }
              ].map((option) => (
                <div
                  key={option.name}
                  className={`d-flex align-items-center gap-3 gap-xl-5 px-3 rounded-2 py-1 cursor-pointer  ${type === option.name
                    ? "bg-white"
                    : "bg-white"
                    }`}
                  onClick={() => setType(option.name)}
                  style={{ minWidth: "120px", border: type === option.name ? "1px solid #4843D3" : "1px solid #dee2e6" }}
                >
                  <span className="d-flex align-items-center gap-2">
                    {option.icon}
                    {option.name}</span>
                  <RadioButton
                    className="radio-single-btn"
                    style={{ marginTop: '3px' }}
                    name="riskAssessmentType"
                    value={option.name}
                    checked={type === option.name}
                    onChange={() => setType(option.name)}

                  />
                </div>
              ))}
            </div>
            <div className="">
              {type === "Client" && (
                <div className="radio d-flex flex-column mt-3 pt-3" style={{ borderTop: ".5px solid #dee2e6a7" }}>
                  <RadioGroup
                    className="d-flex flex-row float-end"
                    style={{ gap: "38px", marginLeft: "2px" }}
                    data={[
                      { label: "Person", value: "Person" },
                      { label: "Company", value: "Company" },
                    ]}
                    value={clientType}
                    onChange={(e) => setClientType(e.value)}
                  />
                </div>
              )}

              {type === "Matter" && (
                <div className="d-flex flex-wrap gap-3 mt-3 pt-2" style={{ borderTop: ".5px solid #dee2e6a7" }}>
                  <FormComboBox
                    wf={true}
                    isStyled={true}
                    width={"230px"}
                    onChangeComboBox={(e) =>
                      handleComboBoxChange(e, "department")
                    }
                    textField="name"
                    label={"Department"}
                    placeholder="Select Department"
                    onFilterChange={departmentfilterChange}
                    data={departmentFilter}
                    state={comboBoxState}
                    name={"department"}
                    loading={loadingDepartments}
                  />
                  <div className="d-flex align-items-center gap-2" >
                    <FormComboBox
                      wf={true}
                      isStyled={true}
                      width={"230px"}
                      onChangeComboBox={(e) =>
                        handleComboBoxChange(e, "workType")
                      }
                      onFilterChange={categoryFilterChange}
                      label="Work Type"
                      placeholder="Select Work Type"
                      data={
                        comboBoxState.department
                          ? categoryFilter.filter(
                            (item) =>
                              item.departmentId ===
                              comboBoxState.department.departmentID
                          )
                          : []
                      }
                      state={comboBoxState}
                      textField="name"
                      name={"workType"}
                      disabled={!comboBoxState.department}
                    />
                    <Tooltip anchorElement="target" position="top">
                      <FiAlertCircle
                        title="Select Department to see corresponding work-types "
                        className="mt-3 cursor-pointer"
                        style={{
                          height: "20px",
                          width: "20px",
                        }}
                      />
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          </div>


          <div className="w-25 bg-white shadow-sm border rounded-3 p-3" style={{ minHeight: "160px" }}>
            <div className="d-flex right-align align-items-center">
              <Label className="label" style={{ width: "150px" }}>
                Low Risk Score
              </Label>
              <Input
                name="low"
                type="number"
                min="0"
                value={riskScores.low}
                onChange={onChangeRiskScores}
                style={{ width: "150px", height: "32px" }}
                placeholder="Add Score"
              />
            </div>
            <div className="d-flex right-align align-items-center mt-2">
              <Label className="label" style={{ width: "150px" }}>
                Medium Risk Score
              </Label>
              <Input
                name="medium"
                type="number"
                min="0"
                value={riskScores.medium}
                onChange={onChangeRiskScores}
                style={{ width: "150px", height: "32px" }}
                placeholder="Add Score"
              />
            </div>
            <div className="d-flex right-align align-items-center mt-2">
              <Label className="label" style={{ width: "150px" }}>
                High Risk Score
              </Label>
              <Input
                name="high"
                type="number"
                min="0"
                value={riskScores.high}
                onChange={onChangeRiskScores}
                style={{ width: "150px", height: "32px" }}
                placeholder="Add Score"
              />
            </div>
          </div>
        </div>

      </div>

      <div className="flex-grow-1 w-100 mx-auto  mt-3" style={{ minHeight: 0, maxWidth: "1400px" }}>
        <div className={`settings-grid users-grid risk-assessment h-100 ${styles.questionsGrid}`} >
          <Grid
            data={gridData.filter((question) => question.active)}
            style={{ height: "100%", maxHeight: 'max-content' }}
            sortable={true}
            className="grid-minwidth-col1-200"
            showLoader={isLoading}
            loader={
              <div style={{ height: "50px" }}>
                <GridLoader />
              </div>
            }
          >
            <GridNoRecords>
              {isLoading ? (
                <span></span>
              ) : (
                gridData && gridData?.length === 0 && "No records available"
              )}
            </GridNoRecords>
            <Column
              field="question"
              title="Questions"
              editable={false}
              className="gridBoldField ps-4"
            />
            <Column
              field="yesWeight"
              title="Yes Weight"
              editable={false}
              className="gridBoldField text-center"
              width={"110px"}
            />
            <Column
              field="noWeight"
              title="No Weight"
              width={"110px"}
              editable={false}
              className="gridBoldField text-center me-"
            />
            <Column
              field="unknownWeight"
              title="Unknown Weight"
              width={"155px"}
              editable={false}
              className="gridBoldField text-center"
            />
            <Column
              title=" "
              width={"80px"}
              cell={CancelCell}
              editable={false}
              className="gridBoldField text-center"
              headerCell={HeaderButton}
            />
          </Grid>
        </div>
      </div>
      <div className={`${styles["buttons-container"]} d-flex align-items-center justify-content-end gap-2 px-4 py-3`}>
        <SubmitButton
          onClick={saveAssessmentData}
          disabled={isSaveDisabled()}
          isLoading={isSubmitting}
        >
          Save
        </SubmitButton>
      </div>

      {openForm && (
        <Dialog onClose={handleCancelEdit} width={1000} title="Select Question">
          <QuestionsPopup
            addQuestions={(questions) => {
              setGridData([...gridData, ...questions]);
              setOpenForm(false);
            }}
            questionIds={gridData
              .filter((question) => question.active)
              .map((question) => question.questionId)}
            type={type}
          />
        </Dialog>
      )}
    </div>
  );
};

export default RiskAssessment;
